import { Directive, Input, TemplateRef, ViewContainerRef } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CustomRegex } from 'shared/constants/custom-regex.const';

@Directive({
  selector: '[appMatErrorMessages]',
})
export class MatErrorMessagesDirective {
  @Input()
  public set appMatErrorMessages(control: AbstractControl | null) {
    this.unsubscribeFromControl();
    this.control = control;
    this.subscribeToControl();
    this.updateView();
  }

  private controlSubscription: Subscription | null = null;
  private control: AbstractControl | null = null;
  private errors!: ValidationErrors;

  constructor(
    private viewContainer: ViewContainerRef,
    private templateRef: TemplateRef<never>,
    private translate: TranslateService,
  ) {}

  private errorMessages: Record<string, (error?: ValidationErrors[keyof ValidationErrors]) => string> = {
    required: () => this.translate.instant('global.validation.required'),
    requireAtLeastOne: () => this.translate.instant('global.validation.requireAtLeastOne'),
    customRequired: (error: string) => this.translate.instant(error),
    customMessage: (error: string) => this.translate.instant(error),
    minlength: (error: MinLengthError) =>
      this.translate.instant('global.validation.minLength', {
        value: error.requiredLength,
      }),
    maxlength: (error: MaxLengthError) =>
      this.translate.instant('global.validation.maxLength', {
        value: error.requiredLength,
      }),
    exactLength: (error: number) =>
      this.translate.instant('global.validation.exactLength', {
        value: error,
      }),
    min: (error: MinError) => this.translate.instant('global.validation.min', { value: error.min }),
    max: (error: MaxError) => this.translate.instant('global.validation.max', { value: error.max }),
    numeric: (error: string) => this.translate.instant(`global.validation.${error}`),
    overlappingRanges: () => this.translate.instant('global.validation.overlappingRanges'),
    pattern: (error: PatternError) => {
      const pattern = this.getPatternName(error.requiredPattern);
      const charRegex = new RegExp(this.stripGlobalFromRegex(error.requiredPattern));
      const nonMatchingChars = [];
      for (const char of error.actualValue) {
        if (!charRegex.test(char)) {
          nonMatchingChars.push(char);
        }
      }
      return this.translate.instant(`global.validation.pattern.${pattern}`, { actual: nonMatchingChars.join('') });
    },
    duplicateName: () => this.translate.instant(`global.validation.duplicateName`),
    email: () => this.translate.instant('user.contact.email'),
    containsString: (error: ContainsTextError) => {
      return this.translate.instant('global.validation.containsString', {
        value: error.requiredSubstring,
      });
    },
    phoneNumber: () => this.translate.instant('global.validation.phoneNumber'),
    default: (error: string) => error,
  };

  private subscribeToControl(): void {
    if (this.control) {
      this.controlSubscription = this.control.statusChanges.subscribe(() => this.updateView());
    }
  }

  private unsubscribeFromControl(): void {
    if (this.controlSubscription) {
      this.controlSubscription.unsubscribe();
    }
  }

  private updateView(): void {
    if (this.control && this.control.errors) {
      this.viewContainer.clear();
      this.errors = this.control.errors;
      this.showError(this.getLastErrorMessage());
    }
  }

  private showError(message: string): void {
    this.viewContainer.createEmbeddedView(this.templateRef, {
      $implicit: message,
    });
  }

  private getLastErrorMessage(): string {
    const errorKeys = Object.keys(this.errors);
    const lastErrorKey = errorKeys[errorKeys.length - 1];

    const error = this.errors[lastErrorKey] as ValidationErrors[keyof ValidationErrors];
    if (this.errorMessages[lastErrorKey]) {
      return this.errorMessages[lastErrorKey](error);
    }

    return 'global.validation.unknown';
  }

  private getPatternName(pattern: string): string | null {
    const normalizedPattern = this.stripDelimiters(pattern);

    for (const [key, value] of Object.entries(CustomRegex)) {
      const regexSource = this.stripDelimiters(value.source);
      if (regexSource === normalizedPattern) {
        return key;
      }
    }
    return null;
  }

  private stripDelimiters(pattern: string): string {
    return pattern.replace(/^[/^]+|[/$]+/g, '');
  }

  private stripGlobalFromRegex(pattern: string): string {
    return pattern.replace(/^\/\^/, '').replace(/\$\/$/, '');
  }
}

interface MinLengthError {
  requiredLength: number;
  actualLength: number;
}

interface MaxLengthError {
  requiredLength: number;
  actualLength: number;
}

interface MinError {
  min: number;
  actual: number;
}

interface MaxError {
  max: number;
  actual: number;
}

interface PatternError {
  requiredPattern: string;
  actualValue: string;
}

interface ContainsTextError {
  requiredSubstring: string;
  actualValue: string;
}
